import { Component, EventEmitter, Input, Output } from '@angular/core';
import Swal from 'sweetalert2';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';

/**
 * Reportar UNA cuota a Seguros Mercantil: el botón, el ensayo previo y la traza.
 *
 * Vive aparte a propósito. La misma acción se dispara desde dos sitios —la cola
 * de "Reporte de Cuotas a Seguros" y la vista del crédito del cliente— y un
 * envío a la aseguradora es irreversible: dos copias del mismo flujo terminan
 * divergiendo, y la que se quede atrás manda dinero mal.
 *
 * Reportar NO envía: abre el ensayo. El envío real exige confirmación explícita
 * y el desenlace sale del summary de SM, nunca del código HTTP — está verificado
 * que un 504 de Mercantil puede esconder un pago ya aplicado.
 *
 * El botón NO se inhabilita por haber enviado antes: la causa dominante de fallo
 * son los timeouts, así que reenviar arregla el caso legítimo. La duplicación la
 * evita el pre-check del backend, que consulta a SM antes de mandar nada.
 */
@Component({
  selector: 'app-insurance-installment-report',
  templateUrl: './insurance-installment-report.component.html'
})
export class InsuranceInstallmentReportComponent {

  /** La clave de todo el flujo: el `code` de la cuota. */
  @Input() installmentCode: string = '';

  /**
   * La póliza de la cuota. Hace falta para la traza: las consultas al summary
   * se auditan por PÓLIZA (una llamada cubre todas sus cuotas), así que sin
   * esto la traza sólo enseñaría los envíos y no la insistencia.
   */
  @Input() policyCode: string | null = null;

  /** Sólo para encabezar los modales; el backend resuelve el resto desde la cuota. */
  @Input() orden: any = null;
  @Input() recibo: string | null = null;
  @Input() polizaNumero: any = null;
  @Input() credito: string | null = null;

  /**
   * Si el host considera que esta cuota admite envío. El ensayo tiene la última
   * palabra: aunque el botón se muestre, los bloqueos deshabilitan el envío.
   */
  @Input() puedeReportar: boolean = true;

  @Input() mostrarTraza: boolean = true;

  /** Texto del botón, para adaptarlo al sitio donde se incrusta. */
  @Input() etiqueta: string = 'Revisar y reportar';

  /** Compacto para listas densas (la cola); normal dentro de la ficha del crédito. */
  @Input() compacto: boolean = true;

  /** Se emite al terminar un envío, con cualquier desenlace: el host recarga. */
  @Output() reportado = new EventEmitter<any>();

  previewAbierto = false;
  preview: any = null;
  previewLoading = false;
  enviando = false;

  auditAbierto = false;
  auditRows: any[] | null = null;

  constructor(
    private _user: UserService,
    public _auth: AuthService
  ) { }

  /**
   * Enviar exige el permiso de reportar.
   *
   * `config.seguros.read` ya NO cuenta aquí, y es a propósito: es un permiso de
   * lectura, riesgo `low`, que venía autorizando un envío irreversible a
   * Mercantil. La guarda de verdad es la del backend
   * (`seguros.cuotas.reportar`); esto sólo decide si el botón se dibuja.
   */
  get puedeEnviar(): boolean {
    return this._auth.hasAnyPermission(['seguros.cuotas.reportar', 'seguros.reporte_pagos.manage']);
  }

  /** La traza es lectura: no cambia nada ni toca a la aseguradora. */
  get puedeVerTraza(): boolean {
    return this._auth.hasAnyPermission([
      'seguros.cuotas.traza', 'seguros.reporte_pagos.read', 'config.seguros.read'
    ]);
  }

  /** Abre el ensayo. No manda nada a la aseguradora. */
  abrirEnsayo(): void {
    if (!this.installmentCode || this.enviando) return;
    this.previewAbierto = true;
    this.preview = null;
    this.previewLoading = true;
    this.enviando = false;
    this._user.previewInsuranceInstallment(this.installmentCode).subscribe({
      next: (r: any) => {
        this.previewLoading = false;
        this.preview = r?.data || null;
      },
      error: (e: any) => {
        this.previewLoading = false;
        this.preview = { bloqueos: [e?.error?.message || e?.message || 'No se pudo simular el envío'] };
      }
    });
  }

  cerrarEnsayo(): void {
    if (this.enviando) return;
    this.previewAbierto = false;
    this.preview = null;
  }

  /** El payload tal cual va a salir, para leerlo antes de que salga. */
  get previewJson(): string {
    return this.preview?.payload ? JSON.stringify(this.preview.payload, null, 2) : '';
  }

  get previewBloqueado(): boolean {
    return !this.preview || (this.preview.bloqueos?.length > 0);
  }

  copiarPayload(): void {
    try {
      navigator.clipboard?.writeText(this.previewJson);
      Swal.fire({ icon: 'success', title: 'Payload copiado', timer: 1200, showConfirmButton: false });
    } catch { /* noop */ }
  }

  /**
   * Envía de verdad, tras confirmación explícita. Puede tardar ~20 s; si el
   * analista cierra la pantalla no se pierde nada, porque el estado se deriva
   * la próxima vez que se abra el caso.
   */
  async confirmarEnvio(): Promise<void> {
    if (!this.installmentCode || this.enviando) return;

    if (this.previewBloqueado) {
      Swal.fire('No permitido', 'El ensayo encontró bloqueos. Resuélvelos antes de reportar.', 'warning');
      return;
    }

    const p = this.preview;
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Reportar esta cuota a Seguros Mercantil?',
      html: `Esta acción <b>notifica el pago a la aseguradora</b>, queda registrada a tu nombre y ` +
            `<b>no se puede deshacer</b>.<br><br>` +
            `Cuota <b>${p?.cuota?.orden}</b> · recibo <b>${p?.cuota?.recibo}</b> · ${p?.cuota?.monto} USD` +
            `<br><small>Antes de enviar se consultará el estado real en SM; si ya está pagada, no se manda nada.</small>`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reportar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      reverseButtons: true
    });
    if (!confirm.isConfirmed) return;

    this.enviando = true;
    this._user.reportInsuranceInstallment(this.installmentCode).subscribe({
      next: (r: any) => {
        this.enviando = false;
        const d = r?.data;
        // El desenlace sale del summary, no del código HTTP.
        if (d?.estado === 'AL_DIA' || d?.estado === 'AL_DIA_POR_EVIDENCIA') {
          Swal.fire({ icon: 'success', title: 'SM la reconoce', text: d?.message || 'La cuota quedó al día.' });
        } else if (d?.estado === 'SIN_CONFIRMAR') {
          Swal.fire({ icon: 'info', title: 'Enviado, sin confirmar',
            text: 'SM aceptó el envío pero todavía no lo da por aplicado. Vuelve a preguntar más tarde.' });
        } else if (d?.estado === 'EN_CURSO') {
          Swal.fire({ icon: 'info', title: 'Ya hay un envío en curso', text: d?.message || '' });
        } else {
          Swal.fire({ icon: 'error', title: 'SM lo rechazó', text: d?.error || 'Revisa la traza de la cuota.' });
        }
        this.previewAbierto = false;
        this.preview = null;
        this.reportado.emit(d);
      },
      error: (e: any) => {
        this.enviando = false;
        Swal.fire('Error', e?.error?.message || e?.message || 'No se pudo reportar la cuota', 'error');
      }
    });
  }

  verTraza(): void {
    if (!this.installmentCode) return;
    this.auditAbierto = true;
    this.auditRows = null;
    this._user.getInsuranceInstallmentAudit(this.installmentCode, this.policyCode || undefined).subscribe({
      next: (r: any) => { this.auditRows = r?.rows || []; },
      error: (e: any) => { console.error('[reporte cuota] traza', e); this.auditRows = []; }
    });
  }

  cerrarTraza(): void {
    this.auditAbierto = false;
    this.auditRows = null;
  }
}
